<script setup>
import { computed, reactive, ref, watch, nextTick, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { DateTime } from 'luxon'

import i18n from '@/plugins/i18n'

import { useNotify } from '@/composables/notify'
import { useDetectDevice } from '@/composables/device'
import { useRecorder } from '@/composables/recorder'

import { useWebSiteStore } from '@/stores/website.store'
import { usePhonebookStore } from '@/stores/phonebook.store'
import { useAgentsStore } from '@/stores/agents.store'
import PhoneBookFormDialog from '@/components/phonebook/PhoneBookFormDialog.vue'

import * as scheduledApi from '@/apis/scheduled.api'

import { defaultItems } from '@vue-js-cron/core'

const MAX_UPLOAD_SIZE_MB = window.config.MAX_UPLOAD_SIZE_MB || 10

const router = useRouter()
const route = useRoute()
const { notifyError, notifySuccess } = useNotify()
const { isMobile } = useDetectDevice()
const websiteStore = useWebSiteStore()
const agentsStore = useAgentsStore()
const phonebookStore = usePhonebookStore()
const t = i18n.global.t
const locale = i18n.global.locale
const items = defaultItems(locale.value)
const { startRecorder, stopRecorder, audioFile } = useRecorder(t('scheduled.create.recordingFilename'))

const getChipColor = (type) => {
  if (type === 'contact') return 'primary'
  if (type === 'group') return 'secondary'
  return 'grey'
}
const showDay = [
  { id: 'year', value: ['month', 'day', 'hour', 'minute'] },
  { id: 'month', value: ['day', 'hour', 'minute'] },
  { id: 'week', value: ['dayOfWeek', 'hour', 'minute'] },
  { id: 'day', value: ['hour', 'minute']},
]

const showWeek = [
  { id: 'year', value: ['month', 'day', 'hour', 'minute'] },
  { id: 'month', value: ['day', 'hour', 'minute'] },
  { id: 'day', value: ['hour', 'minute']},
  { id: 'week', value: ['dayOfWeek', 'hour', 'minute'] },
]
const showMonth = [
  { id: 'year', value: ['month', 'day', 'hour', 'minute'] },
  { id: 'week', value: ['dayOfWeek', 'hour', 'minute'] },
  { id: 'day', value: ['hour', 'minute']},
  { id: 'month', value: ['day', 'hour', 'minute'] },
]
const showYear = [
  { id: 'month', value: ['day', 'hour', 'minute'] },
  { id: 'week', value: ['dayOfWeek', 'hour', 'minute'] },
  { id: 'day', value: ['hour', 'minute']},
  { id: 'year', value: ['month', 'day', 'hour', 'minute'] },
]

const adaptPeriods = () => {
  if (!form.crontabExpression || form.crontabExpression === '* * * * *') {
    return showDay
  }
  const parts = form.crontabExpression.split(' ')
  if (parts.length !== 5) {
    return 'minute'
  }

  // eslint-disable-next-line no-unused-vars
  const [minute, hour, day, month, dayOfWeek] = parts

  if (month !== '*') {
    return showYear
  } else if (day !== '*') {
    return showMonth
  } else if (dayOfWeek !== '*') {
    return showWeek
  }
  return showDay
}

const onPresetChange = (val) => {
  loading.value = true
  if (val === 'custom') {
    form.crontabExpression = '* * * * *'
  } else {
    form.crontabExpression = val
  }
  nextTick(() => { // Rebuild the cron-light component to reflect the change in periods
    selectedPeriods.value = adaptPeriods()
    loading.value = false
  })
}

const recipientsRule = (val) => {
  if (!val || val.length === 0) return t('scheduled.create.error.recipientRequired')
  return true
}

const validateFileSize = (file) => {
  if (!file) return true
  const maxBytes = MAX_UPLOAD_SIZE_MB * 1024 * 1024
  return file.size <= maxBytes || t('scheduled.notifications.errorFileSizeExceeded', { max: MAX_UPLOAD_SIZE_MB })
}
const buildFormData = (form) => {
  const fd = new FormData()

  fd.append('sender_agent_id', form.agent?.value ?? '')
  fd.append('content', form.content ?? '')
  fd.append('name', form.name ?? '')
  fd.append('active', form.active)
  fd.append('timezone', websiteStore.userData.default_timezone || 'UTC')

  if (form.crontabExpression) {
    fd.append('crontab_expression', form.crontabExpression)
  } else {
    fd.append('scheduled_time', toISO(form.scheduledTime) ?? '')
  }

  if (form.attachment) {
    fd.append('attachment', form.attachment)
    fd.append('attachment_name', form.attachment?.name)
  }
  fd.append('delete_attachment', deleteAttachment.value)

  form.recipientContacts.forEach(id => fd.append('recipient_contacts', id))
  form.recipientGroups.forEach(id => fd.append('recipient_groups', id))
  form.recipientNumbers.forEach(n => fd.append('recipient_numbers', n))

  return fd
}

const toISO = (value) => {
  if (!value) return null
  const tz = websiteStore.userData.default_timezone || 'UTC'
  return DateTime
    .fromFormat(value, 'yyyy-MM-dd HH:mm', { zone: tz })
    .toISO()
}

const toNaive = (value) => {
  const tz = websiteStore.userData.default_timezone || 'UTC'
  return DateTime
    .fromISO(value, { zone: tz })
    .toFormat('yyyy-MM-dd HH:mm')
}

const isPastDate = (value) => {
  if (!value) return false
  const selected = DateTime.fromFormat(value, 'yyyy-MM-dd HH:mm', { zone: userZone() })
  return selected < userNow()
}

const userZone = () => websiteStore.userData.default_timezone || 'UTC'

// "Adesso" non puo' essere fotografato una volta sola: un dialogo lasciato aperto dieci minuti
// continuerebbe a considerare validi orari nel frattempo passati. Questo contatore fa
// ricalcolare gli orari ammessi ogni 30 secondi finche' il dialogo e' aperto.
const nowTick = ref(Date.now())
let nowTimer = null

const userNow = () => DateTime.fromMillis(nowTick.value).setZone(userZone())

const startNowTicker = () => {
  stopNowTicker()
  nowTick.value = Date.now()
  nowTimer = setInterval(() => { nowTick.value = Date.now() }, 30000)
}

const stopNowTicker = () => {
  if (nowTimer) {
    clearInterval(nowTimer)
    nowTimer = null
  }
}

// Il campo parte da un orario gia' valido (adesso + 5 minuti nel fuso dell'utente): cosi' il
// selettore ha da subito un giorno di riferimento e il filtro sul passato e' attivo al primo clic.
const defaultScheduledTime = () => DateTime.local().setZone(userZone()).plus({ minutes: 5 }).startOf('minute').toFormat('yyyy-MM-dd HH:mm')

const props = defineProps({
  itemId: {
    type: [String, Number],
    required: false
  },
  fullScreen: {
    type: Boolean,
    default: false
  },
  title: {
    type: String,
    default: 'scheduled.create.title'
  },
  mode: {
    type: String,
    required: true,
    validator: value => ['edit', 'create'].includes(value)
  },
  allAgents: {
    type: Array,
    required: false,
    default: () => []
  }
})

const emit = defineEmits(['update:modelValue', 'saved'])

const cronPresets = computed(() => [
  { label: t('scheduled.create.customCronSelected'), value: 'custom' },
  { label: t('scheduled.create.cronPresets.everyDayAtMidnight'), value: '0 0 * * *' },
  { label: t('scheduled.create.cronPresets.everyMondayAt9am'), value: '0 9 * * 1' },
  { label: t('scheduled.create.cronPresets.everyFirstOfMonthAt10am'), value: '0 10 1 * *' },
])

const isPreset = computed(() => {
  return cronPresets.value.some(opt => opt.value === form.crontabExpression)
})

const isMidNight = computed(() => form.scheduledTime && form.scheduledTime.endsWith('00:00'))

const loading = ref(true)
const scheduledForm = ref(null)
const combinedOptions = ref([])
const groupsLoading = ref(false)
const rawGroups = ref([])
const fileName = ref(null)
const deleteAttachment = ref(false)
const isRecording = ref(false)
const selectedMessageType = ref('recurring')
const showInfo = ref(false)
const showCreateRecipient = ref(false)
const selectedPeriods = ref(showDay)

const form = reactive({
  name: '',
  agent: null,
  selectedRecipients: [],
  content: '',
  crontabExpression: '* * * * *',
  scheduledTime: null,
  active: false,
  attachment: null,
})

const initialData = reactive({})
const show = computed({
  get: () => props.mode === 'edit' ? !!route.query.edit : !!route.query.create,
  set: value => { if (!value) closeDialog() }
})

const hasChanges = computed(() => {
  if (props.mode === 'create') return true
  return JSON.stringify(form) !== JSON.stringify(initialData)
})

const recordButtonIcon = computed(() => {
  if (isRecording.value) {
    return 'mdi-stop'
  } else {
    return 'mdi-microphone'
  }
})

const fileInputLabel = computed(() => {
  if (isRecording.value) {
    return t('scheduled.create.recordingInProgress')
  }
  return t('scheduled.create.attachment')
})

const dateOptions = computed(() => {
  const now = userNow()
  return (date) => {
    const dateTime = DateTime.fromFormat(date, 'yyyy/MM/dd', { zone: userZone() })
    return dateTime >= now.startOf('day')
  }
})

const timeOptions = computed(() => {
  const now = userNow()

  // Il giorno di riferimento e' quello gia' scelto; se il campo e' ancora vuoto il selettore
  // parte comunque da oggi, quindi si filtra su oggi fin dal PRIMO render.
  // Prima qui c'era "if (!form.scheduledTime) return true": con il campo vuoto nessun orario
  // veniva escluso, ed e' il motivo per cui il passato spariva solo al secondo clic.
  const selected = form.scheduledTime
    ? DateTime.fromFormat(form.scheduledTime, 'yyyy-MM-dd HH:mm', { zone: userZone() })
    : now

  return (hour, minute) => {
    if (!selected.isValid) return true

    // Giorno diverso da oggi: futuro tutto valido, passato tutto escluso.
    if (!selected.hasSame(now, 'day')) return selected.startOf('day') > now.startOf('day')

    if (hour > now.hour) return true
    if (hour < now.hour) return false

    // Stessa ora di adesso: valgono solo i minuti non ancora passati.
    // (Nella vista delle ore Quasar passa minute = null: li' basta che l'ora sia quella corrente.)
    return minute === null || minute === undefined || minute >= now.minute
  }
})

const resetForm = (data = {}) => {
  deleteAttachment.value = false
  fileName.value = data.attachment_name || null
  selectedMessageType.value = data.scheduledTime ? 'one-time' : 'recurring'
  Object.assign(form, {
    name: '',
    agent: null,
    selectedRecipients: [],
    content: '',
    crontabExpression: '* * * * *',
    scheduledTime: props.mode === 'create' ? defaultScheduledTime() : null,
    active: false,
    attachment: null,
    ...data
  })
}

const removeFile = () => {
  form.attachment = null
  fileName.value = null
  deleteAttachment.value = true
}

const fetchMessageDetailAsync = async() => {
  if (props.mode !== 'edit') return
  if (!props.itemId) return

  try {
    const { data } =
      await scheduledApi.fetchScheduledMessageDetailAsync(props.itemId)

    const mergedRecipients = []

    if (data.recipient_contacts) {
      data.recipient_contacts.forEach(c => {
        mergedRecipients.push({
          label: c.name || c.phone,
          value: c.id,
          type: 'contact',
          icon: 'mdi-account'
        })
      })
    }

    if (data.recipient_groups) {
      data.recipient_groups.forEach(g => {
        mergedRecipients.push({
          label: g.display_name,
          value: g.id,
          type: 'group',
          icon: 'mdi-user-group'
        })
      })
    }

    if (data.recipient_numbers) {
      data.recipient_numbers.forEach(n => {
        mergedRecipients.push({
          label: n,
          value: n,
          type: 'manual',
          icon: 'mdi-keypad'
        })
      })
    }

    const mapped = {
      name: data.name,
      agent: data.sender_agent
        ? { label: data.sender_agent.name, value: data.sender_agent.id }
        : null,
      selectedRecipients: mergedRecipients,
      content: data.content,
      crontabExpression: data.crontab_expression || '* * * * *',
      scheduledTime: data.scheduled_time ? toNaive(data.scheduled_time) : null,
      active: data.active,
      attachment_name: data.attachment_name,
    }
    resetForm(mapped)
    Object.assign(initialData, JSON.parse(JSON.stringify(mapped)))
  } catch (error) {
    console.error(error)
    notifyError(t('scheduled.notifications.errorFetchingDetails'))
  }
}

const filterRecipients = (val, update) => {
  if (!phonebookStore.loaded) {
    update(async() => {
      try {
        await phonebookStore.fetchContacts()
        performCombinedFilter(val, update)
      } catch (error) {
        notifyError(t('scheduled.notifications.errorFetchingRecipients'))
        combinedOptions.value = []
      }
    })
    return
  }

  performCombinedFilter(val, update)
}

const performCombinedFilter = (val, update) => {
  update(() => {
    const needle = val.toLowerCase().trim()
    let result = []

    const contacts = phonebookStore.allContacts.map(contact => ({
      label: contact.name,
      subLabel: contact.phone_number,
      value: contact.id,
      type: 'contact',
      icon: 'mdi-account',
      searchable: `${contact.name} ${contact.phone_number}`.toLowerCase()
    }))

    const groups = rawGroups.value.map(g => ({
      label: g.display_name,
      value: g.id,
      type: 'group',
      icon: 'mdi-account-group',
      searchable: g.display_name.toLowerCase()
    }))

    const allSource = [...contacts, ...groups]

    if (needle === '') {
      result = allSource
    } else {
      result = allSource.filter(opt => opt.searchable.includes(needle))
    }

    combinedOptions.value = result
  })
}

const save = async() => {
  loading.value = true

  const isValid = await scheduledForm.value.validate()
  if (!isValid) {
    notifyError(t('form.notifications.fixErrors'))
    loading.value = false
    return
  }

  // send just one scheduling option
  if (selectedMessageType.value === 'recurring') {
    form.scheduledTime = null
  } else {
    form.crontabExpression = null
  }
  // At least cron or scheduled time is required
  if (selectedMessageType.value === 'recurring' && !form.crontabExpression) {
    notifyError(t('scheduled.create.error.cronRequired'))
    loading.value = false
    return
  } else if (selectedMessageType.value === 'one-time' && !form.scheduledTime) {
    notifyError(t('scheduled.create.error.scheduledTimeRequired'))
    loading.value = false
    return
  }
  // At least content or attachment is required
  if (!form.content && !fileName.value) {
    notifyError(t('scheduled.create.error.contentOrAttachmentRequired'))
    loading.value = false
    return
  }
  // A recording cannot have text content
  if (fileName.value && fileName.value.endsWith('.mp3') && form.content) {
    notifyError(t('scheduled.create.error.recordingNoText'))
    loading.value = false
    return
  }

  form.recipientContacts = []
  form.recipientGroups = []
  form.recipientNumbers = []

  form.selectedRecipients.forEach(item => {
    if (item.type === 'contact') {
      form.recipientContacts.push(item.value)
    } else if (item.type === 'group') {
      form.recipientGroups.push(item.value)
    } else {
      form.recipientNumbers.push(item.value)
    }
  })

  // Id del messaggio appena creato: serve alla lista per segnalare che il nuovo messaggio
  // nasce spento e va attivato. Resta null se il salvataggio fallisce (l'emit sta nel finally).
  let createdId = null

  try {
    const payload = buildFormData(form)

    if (props.mode === 'edit') {
      await scheduledApi.updateScheduledMessageAsync(props.itemId, payload)
    } else {
      const { data } = await scheduledApi.createScheduledMessageAsync(payload)
      createdId = data?.id ?? null
    }

    notifySuccess(t('scheduled.notifications.savedSuccessfully'))
    closeDialog()
  } catch (error) {
    if (error.response?.status === 500) {
      notifyError(t('error.serverError'))
      return
    }
    const errors = error.response?.data || {}
    for (const field in errors) {
      if (errors.hasOwnProperty(field)) {
        errors[field].forEach((errorMsg) => {
          notifyError(t(errorMsg))
        })
      }
    }
  } finally {
    emit('saved', createdId)
    loading.value = false
  }
}

const closeDialog = () => {
  const query = { ...route.query }

  if (props.mode === 'edit') delete query.edit
  if (props.mode === 'create') delete query.create

  router.replace({ query })
}

const handleRecording = async() => {
  if (isRecording.value) {
    await stopRecorder()
    isRecording.value = false
    form.attachment = audioFile.value
    fileName.value = audioFile.value.name
  } else {
    startRecorder()
    isRecording.value = true
  }
}

const onSaveContact = async() => {
  showCreateRecipient.value = false
  await phonebookStore.fetchContacts()
}

const onCloseContactDialog = () => {
  showCreateRecipient.value = false
}

watch(
  () => [props.itemId, props.mode],
  async([newItemId, newMode]) => {
    loading.value = true
    if (newMode === 'edit' && !newItemId) {
      loading.value = false
      return
    }

    await fetchMessageDetailAsync()

    selectedPeriods.value = adaptPeriods()
    loading.value = false
  },
  { immediate: true }
)

watch(() => form.agent, async(newAgent, oldAgent) => {
  if (!newAgent) {
    rawGroups.value = []
    form.selectedRecipients = []
    return
  }

  if (newAgent.value === oldAgent?.value) return

  if (oldAgent) {
    form.selectedRecipients = []

    rawGroups.value = []
  }

  try {
    groupsLoading.value = true
    const data = await agentsStore.fetchGroups(newAgent.value)
    rawGroups.value = data
  } catch (error) {
    notifyError(t('scheduled.notifications.errorFetchingGroups'))
    rawGroups.value = []
  } finally {
    groupsLoading.value = false
  }
}, { immediate: true })

watch(show, (isOpen) => {
  if (isOpen) {
    startNowTicker()
    // In creazione l'orario proposto va ricalcolato a ogni apertura, altrimenti resterebbe
    // quello del primo montaggio del dialogo (e sarebbe gia' passato).
    if (props.mode === 'create') form.scheduledTime = defaultScheduledTime()
    return
  }
  stopNowTicker()
  resetForm()
})

onUnmounted(stopNowTicker)
</script>

<template>
  <q-dialog
    v-model="show"
    :maximized="fullScreen || isMobile"
  >
    <q-card
      v-touch-swipe.mouse.right="closeDialog"
      class="dialog-card"
    >
      <!-- Header -->
      <q-card-section class="dialog-header bg-primary text-white row items-center">
        <div class="col">
          <div class="text-h6">
            {{ $t(props.title) }}
          </div>
        </div>
      </q-card-section>

      <q-separator />

      <!-- Body -->
      <div class="dialog-body q-pa-md">
        <q-form
          ref="scheduledForm"
          class="q-gutter-md"
          @submit.prevent="save"
        >
          <!-- Name & Agent -->
          <q-input
            v-model="form.name"
            :label="$t('scheduled.create.name')"
            required
            :rules="[val => !!val || $t('form.field.required')]"
          />

          <q-select
            v-model="form.agent"
            :options="props.allAgents.map(agent => ({ label: websiteStore.getAgentName(agent.id), value: agent.id }))"
            :label="$t('scheduled.create.agent')"
            required
            :rules="[val => !!val || $t('form.field.required')]"
          />

          <!-- Recipient  -->
          <q-select
            v-model="form.selectedRecipients"
            :label="$t('scheduled.create.recipients')"
            :options="combinedOptions"
            multiple
            use-input
            use-chips
            input-debounce="500"
            :rules="[recipientsRule]"
            :loading="groupsLoading"
            :disable="!form.agent"
            :hint="!form.agent ? $t('scheduled.create.selectAgentFirst') : undefined"
            @filter="filterRecipients"
          >
            <template #option="scope">
              <q-item v-bind="scope.itemProps">
                <q-item-section avatar>
                  <q-icon :name="scope.opt.icon" />
                </q-item-section>
                <q-item-section>
                  <q-item-label>{{ scope.opt.label }}</q-item-label>
                  <q-item-label
                    v-if="scope.opt.subLabel"
                    caption
                  >
                    {{ scope.opt.subLabel }}
                  </q-item-label>
                </q-item-section>
              </q-item>
            </template>

            <template #selected-item="scope">
              <q-chip
                dense
                outline
                removable
                :tabindex="scope.tabindex"
                :color="getChipColor(scope.opt.type)"
                class="q-ma-none q-mr-xs"
                @remove="scope.removeAtIndex(scope.index)"
              >
                {{ scope.opt.label }}
              </q-chip>
            </template>

            <template #no-option>
              <q-item>
                <q-item-section class="text-grey">
                  {{ $t('common.noResults') }}
                </q-item-section>
              </q-item>
            </template>
            <template #append>
              <q-btn
                round
                dense
                flat
                icon="mdi-plus"
                @click.stop="showCreateRecipient = true"
              />
            </template>
          </q-select>
          <!-- Phonebook Dialog -->
          <PhoneBookFormDialog
            v-model="showCreateRecipient"
            mode="create"
            :full-screen="isMobile"
            title="phonebook.create.title"
            :handle-close="false"
            @saved="onSaveContact"
            @closed="onCloseContactDialog"
          />

          <!-- Select message periodicity -->
          <div>
            <q-radio
              v-model="selectedMessageType"
              :label="$t('scheduled.create.messageType.recurring')"
              :val="'recurring'"
            />
            <q-radio
              v-model="selectedMessageType"
              :label="$t('scheduled.create.messageType.oneTime')"
              :val="'one-time'"
            />
          </div>
          <q-banner
            class="info_menu q-mb-sm"
            dense
            rounded
          >
            {{ $t('scheduled.create.selectedTimezone') }}:
            {{ websiteStore.userData.default_timezone }}
          </q-banner>
          <!-- Crontab Expression -->
          <div
            v-if="selectedMessageType === 'recurring'"
            class="q-gutter-y-md"
          >
            <q-select
              v-model="form.crontabExpression"
              :label="$t('scheduled.create.cronPresets')"
              :options="cronPresets"
              emit-value
              map-options
              :display-value="isPreset ? undefined : $t('scheduled.create.customCronSelected')"
              @update:model-value="onPresetChange"
            />

            <q-field
              outlined
              stack-label
              :label="$t('scheduled.create.cron')"
              bg-color="white"
              required
            >
              <template #control>
                <div class="self-center full-width">
                  <cron-light
                    v-if="form.crontabExpression && !loading"
                    v-model="form.crontabExpression"
                    :locale="locale"
                    :periods="selectedPeriods"

                    :fields="[
                      { id: 'minute', items: items.minuteItems },
                      { id: 'hour', items: items.hourItems },
                      { id: 'day', items: items.dayItems },
                      { id: 'month', items: items.monthItems },
                      { id: 'dayOfWeek', items: items.dayOfWeekItems },
                    ]"
                  />
                  <div class="absolute-bottom-right q-pa-xs">
                    <q-btn
                      round
                      flat
                      dense
                      icon="mdi-information-outline"
                      size="md"
                      class="text-grey-7 q-pa-sm"
                      @click.stop="showInfo = !showInfo"
                    />

                    <q-menu
                      v-model="showInfo"
                      class="info_menu"
                      anchor="bottom right"
                      self="top right"
                      :offset="[0, 8]"
                      auto-close
                    >
                      <q-card style="max-width: 90vw; width: 320px;background-color: #e0f4ff;">
                        <q-card-section class="text-body2">
                          {{ $t('scheduled.create.nextFiveRuns') }}<br>
                          <ul class="q-pl-md q-mt-sm">
                            <li
                              v-for="(run, index) in form.crontabExpression ? websiteStore.computeNextRun(true, form.crontabExpression, null ,websiteStore.userData.default_timezone, locale, 5 ) : []"
                              :key="index"
                            >
                              {{ run }}
                            </li>
                          </ul>
                        </q-card-section>
                      </q-card>
                    </q-menu>
                  </div>
                </div>
              </template>
            </q-field>
          </div>
          <!-- Datetime selector -->
          <div
            v-else-if="selectedMessageType === 'one-time'"
            class="q-gutter-y-md"
          >
            <q-input
              v-model="form.scheduledTime"
              :rules="[val => isPastDate(val) ? $t('scheduled.create.error.scheduledTimeInPast') : true]"
            >
              <template #prepend>
                <q-icon
                  name="mdi-calendar"
                  class="cursor-pointer"
                >
                  <q-popup-proxy
                    cover
                    transition-show="scale"
                    transition-hide="scale"
                  >
                    <q-date
                      v-model="form.scheduledTime"
                      mask="YYYY-MM-DD HH:mm"
                      :options="dateOptions"
                    >
                      <div class="row items-center justify-end">
                        <q-btn
                          v-close-popup
                          label="Close"
                          color="primary"
                          flat
                        />
                      </div>
                    </q-date>
                  </q-popup-proxy>
                </q-icon>
              </template>

              <template #append>
                <q-icon
                  name="mdi-clock-time-eight-outline"
                  :class="{ 'cursor-pointer text-primary': !isMidNight, 'blink-icon-slow': isMidNight }"
                >
                  <q-popup-proxy
                    cover
                    transition-show="scale"
                    transition-hide="scale"
                  >
                    <q-time
                      v-model="form.scheduledTime"
                      mask="YYYY-MM-DD HH:mm"
                      format24h
                      :options="timeOptions"
                    >
                      <div class="row items-center justify-end">
                        <q-btn
                          v-close-popup
                          label="Close"
                          color="primary"
                          flat
                        />
                      </div>
                    </q-time>
                  </q-popup-proxy>
                </q-icon>
              </template>
            </q-input>
          </div>
          <!-- Text + attachment -->
          <div>
            <q-input
              v-model="form.content"
              :label="$t('scheduled.create.message')"
              type="textarea"
              required
              class="q-pb-md"
            />

            <div>
              <div
                v-if="fileName"
                class="row items-center no-wrap"
                style="flex: 1;"
              >
                <q-field
                  outlined
                  readonly
                  :model-value="fileName"
                  style="flex: 1; min-width: 0;"
                >
                  <template #prepend>
                    <q-icon name="mdi-paperclip" />
                  </template>

                  <template #control>
                    <div class="ellipsis">
                      {{ fileName }}
                    </div>
                  </template>
                </q-field>

                <q-btn
                  dense
                  flat
                  round
                  icon="mdi-close"
                  class="q-ml-sm"
                  style="flex-shrink: 0;"
                  @click="removeFile"
                />
              </div>

              <div
                v-else
                class="row items-start"
              >
                <q-file
                  v-model="form.attachment"
                  outlined
                  :label="fileInputLabel"
                  :rules="[validateFileSize]"
                  :disable="isRecording"
                  style="flex: 1;"
                  @update:model-value="fileName = $event?.name ?? null"
                >
                  <template #prepend>
                    <q-icon
                      :name="isRecording ? 'mdi-record' : 'mdi-paperclip'"
                      :class="{ 'blink-icon': isRecording }"
                    />
                  </template>
                </q-file>

                <q-btn
                  dense
                  flat
                  round
                  class="q-mt-md"
                  :icon="recordButtonIcon"
                  @click="handleRecording"
                />
              </div>
            </div>
          </div>
        </q-form>
      </div>

      <!-- Footer -->
      <q-card-actions class="dialog-footer q-gutter-sm bg-white">
        <q-btn
          :label="$t('common.cancel')"
          flat
          :loading="loading"
          @click="closeDialog"
        />
        <q-btn
          color="primary"
          :loading="loading"
          :disabled="!hasChanges || loading"
          type="submit"
          :label="$t('common.save')"
          @click="save"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>
